/* eslint-disable no-useless-escape */
import { create } from "zustand";
import $api from "@/zapi-frontend/shared/api/axios";
import { TGetProjectResponse, TChapter } from "../shared/types/projects";
import { analyzeChapterChanges } from "../utils/analyze-chapter-changes";
import { TSidebarChapter } from "../pages/ProjectDetailsPage/model/types/types";

type TCroppedMemoImage = {
  id: number;
  imageUrl: string;
  tlCoordinateX: number;
  tlCoordinateY: number;
  brCoordinateX: number;
  brCoordinateY: number;
};

export type ViewMode = "chapter" | "dashboard" | "projectOverview" | "instructions";

type TChapterStore = {
  // state
  projectId: string | null;
  projectChapters: TChapter[];
  activeChapter: TChapter | null;
  isOpenChapterSidebar: boolean;
  viewMode: ViewMode;
  sidebarChaptersData: TSidebarChapter[];
  activeSidebarChapterData: TSidebarChapter | null;
  activeChapterSubsection: string | null;
  activeSyncRequests: Set<string>;

  // actions
  toggleChapterSidebar: (isOpen: boolean) => void;
  setViewMode: (mode: ViewMode) => void;
  setSidebarChaptersData: (chapters: TSidebarChapter[], activeChapterId: string | null) => void;
  setActiveChapterSubsection: (subsection: string | null) => void;
  setActiveChapter: (chapterId: string) => void;
  updateChapterContent: (chapterId: string, content: string, shouldCheckCurrent?: boolean) => void;
  updateChapterCurrent: (chapterId: string, current: boolean) => void;
  updateChapterCompleteness: (params: {
    chapterId: string;
    completeness: number;
    valid: number;
    total: number;
  }) => void;
  updateChapterCroppedMemoImage: (chapterId: string, imageData: TCroppedMemoImage) => Promise<void>;
  invalidateChapterCroppedImages: (chapterId: string) => Promise<void>;
  getCropCoordinatesForImage: (id: number) => TCroppedMemoImage | null;
  initializeProjectChapters: (project: TGetProjectResponse) => void;
  syncChapterContent: (chapterId: string, abortController?: AbortController) => Promise<string | null>;
  getChapterContent: (chapterId: string) => string | null;
  updateChapter: (params: { chapterId: number; value: Partial<TChapter> }) => void;

  updateChapterContentUpdatedAt: (chapterId: string, contentUpdatedAt: string) => void;
};

export const useChapterStore = create<TChapterStore>((set, get) => ({
  projectId: null,
  projectChapters: [],
  activeChapter: null,
  isOpenChapterSidebar: false,
  viewMode: "chapter",
  sidebarChaptersData: [],
  activeSidebarChapterData: null,
  activeChapterSubsection: null,
  activeSyncRequests: new Set(),

  toggleChapterSidebar: (isOpen) => {
    set({ isOpenChapterSidebar: isOpen });
  },
  setViewMode: (mode) => {
    set({ viewMode: mode });
    if (mode === "dashboard" || mode === "projectOverview") {
      set({ activeChapter: null, activeSidebarChapterData: null });
    }
  },
  setSidebarChaptersData: (chapters, activeChapterId) => {
    const activeChapter = chapters.find((c) => c.id === activeChapterId) ?? null;
    set({ sidebarChaptersData: chapters, activeSidebarChapterData: activeChapter });
  },
  setActiveChapterSubsection: (subsection) => {
    set({ activeChapterSubsection: subsection });
  },
  setActiveChapter: (chapterId) => {
    set((state) => {
      const newChapter = state.projectChapters.find((c) => c.id === Number(chapterId));
      const newSidebarChapter = state.sidebarChaptersData.find((c) => c.id === chapterId);
      const activeSubsection = state.activeChapterSubsection;
      const isSubsectionValid = newSidebarChapter?.subsections.includes(activeSubsection || "") ?? null;

      if (newChapter)
        return {
          activeChapter: newChapter,
          activeSidebarChapterData: newSidebarChapter,
          activeChapterSubsection: isSubsectionValid ? activeSubsection : newSidebarChapter?.subsections[0],
          viewMode: "chapter" as ViewMode
        };

      return state;
    });
  },
  updateChapter: ({ chapterId, value }) => {
    set((state) => {
      const updatedChapters = state.projectChapters.map((chapter) =>
        chapter.id === chapterId ? { ...chapter, ...value } : chapter
      );
      return {
        projectChapters: updatedChapters,
        activeChapter:
          state.activeChapter?.id === chapterId ? { ...state.activeChapter, ...value } : state.activeChapter
      };
    });
  },

  updateChapterContent: (chapterId, content, shouldCheckCurrent = false) => {
    const { projectChapters, activeChapter, setActiveChapter } = get();
    const updatedChapter = projectChapters.find((c) => c.id === Number(chapterId));
    if (!updatedChapter) {
      return;
    }
    const oldContent = updatedChapter.activeMemo?.mdContent;
    const newContent = content;
    const isDifferent = analyzeChapterChanges(oldContent, newContent);
    const isCurrent = shouldCheckCurrent ? (isDifferent ? false : updatedChapter.isCurrent) : updatedChapter.isCurrent;
    const newChapter = {
      ...updatedChapter,
      activeMemo: updatedChapter.activeMemo
        ? {
            ...updatedChapter.activeMemo,
            mdContent: content,
            contentUpdatedAt: updatedChapter.activeMemo.contentUpdatedAt
          }
        : null,
      isCurrent
    };

    // If this is not the Executive Summary and content has changed, mark Executive Summary as stale
    const isExecutiveSummary = updatedChapter.projectTemplateChapter.name === "Executive Summary";
    let updatedChapters = projectChapters.map((chapter) => (chapter.id === Number(chapterId) ? newChapter : chapter));

    if (!isExecutiveSummary && isDifferent && oldContent !== newContent) {
      // Mark Executive Summary as not current when other chapters are updated
      updatedChapters = updatedChapters.map((chapter) =>
        chapter.projectTemplateChapter.name === "Executive Summary" ? { ...chapter, isCurrent: false } : chapter
      );
    }

    set(() => {
      return {
        projectChapters: updatedChapters
      };
    });
    if (Number(chapterId) === activeChapter?.id) {
      setActiveChapter(chapterId);
    }
  },
  updateChapterCurrent: (chapterId, current) => {
    set((state) => {
      return {
        projectChapters: state.projectChapters.map((chapter) =>
          chapter.id === Number(chapterId) ? { ...chapter, isCurrent: current } : chapter
        )
      };
    });
    if (Number(chapterId) === get().activeChapter?.id) {
      get().setActiveChapter(chapterId);
    }
  },
  updateChapterCompleteness: ({ chapterId, completeness, valid, total }) => {
    set((state) => {
      return {
        activeChapter:
          state.activeChapter?.id === Number(chapterId)
            ? {
                ...state.activeChapter,
                completenessRatio: completeness,
                validQuestionsCount: valid,
                totalQuestions: total
              }
            : state.activeChapter,
        projectChapters: state.projectChapters.map((chapter) =>
          chapter.id === Number(chapterId)
            ? { ...chapter, completenessRatio: completeness, validQuestionsCount: valid, totalQuestions: total }
            : chapter
        )
      };
    });
  },
  updateChapterCroppedMemoImage: async (chapterId, imageData) => {
    const { projectId, projectChapters } = get();
    if (!projectId) {
      throw new Error("Project ID not found");
    }

    const updatedChapter = projectChapters.find((c) => c.id === Number(chapterId));
    if (!updatedChapter) {
      return;
    }

    const oldImagesData = updatedChapter.activeMemo?.croppedMemoImages;
    const newImagesData = [...(oldImagesData || []).filter((image) => image.id !== imageData.id), imageData];

    const newChapter: TChapter = {
      ...updatedChapter,
      activeMemo: updatedChapter.activeMemo ? { ...updatedChapter.activeMemo, croppedMemoImages: newImagesData } : null
    };

    set((state) => {
      return {
        projectChapters: state.projectChapters.map((chapter) =>
          chapter.id === Number(chapterId) ? newChapter : chapter
        )
      };
    });

    // Update active chapter if it's the one that was modified
    if (Number(chapterId) === get().activeChapter?.id) {
      get().setActiveChapter(chapterId);
    }

    try {
      await $api.patch(`/projects/${projectId}/chapters/${chapterId}/cropped-memo-image`, imageData);
    } catch (error) {
      set((state) => {
        return {
          projectChapters: state.projectChapters.map((chapter) =>
            chapter.id === Number(chapterId) ? updatedChapter : chapter
          )
        };
      });
      console.error("Failed to update cropped memo image:", error);
      throw error;
    }
  },
  invalidateChapterCroppedImages: async (chapterId) => {
    const { projectId, setActiveChapter, activeChapter } = get();
    if (!projectId) {
      return;
    }

    try {
      const response = await $api.get(`/projects/${projectId}/chapters/${chapterId}/cropped-memo-image`);
      const croppedMemoImages = response.data as TCroppedMemoImage[];

      set((state) => {
        return {
          projectChapters: state.projectChapters.map((chapter) =>
            chapter.id === Number(chapterId)
              ? {
                  ...chapter,
                  activeMemo: chapter.activeMemo
                    ? {
                        ...chapter.activeMemo,
                        croppedMemoImages
                      }
                    : null
                }
              : chapter
          )
        };
      });

      if (Number(chapterId) === activeChapter?.id) {
        setActiveChapter(chapterId);
      }
    } catch (error) {
      console.error("Failed to invalidate chapter cropped images:", error);
      throw error;
    }
  },
  getCropCoordinatesForImage: (id) => {
    const { activeChapter } = get();
    if (!activeChapter?.activeMemo?.croppedMemoImages) {
      return null;
    }

    const croppedMemoImage = activeChapter.activeMemo.croppedMemoImages.find((i) => i.id === id);
    return croppedMemoImage || null;
  },
  initializeProjectChapters: async (project: TGetProjectResponse) => {
    const { activeChapter } = get();
    const chapters = project.projectChapters;

    set({
      projectId: project.id.toString(),
      projectChapters: chapters
    });

    if (activeChapter) {
      const updatedActiveChapter = chapters.find((c) => c.id === activeChapter.id);
      if (updatedActiveChapter) {
        set({ activeChapter: updatedActiveChapter });
      }
    }
  },
  syncChapterContent: async (chapterId, abortController?: AbortController) => {
    const { projectId, projectChapters, updateChapterContent, activeSyncRequests } = get();
    if (!projectId || !chapterId) return null;
    const chapter = projectChapters.find((c) => c.id === Number(chapterId));

    if (!chapter || !chapter.activeMemo) return null;

    const requestKey = `${projectId}-${chapterId}`;
    if (activeSyncRequests.has(requestKey)) return null;

    set({ activeSyncRequests: new Set([...activeSyncRequests, requestKey]) });
    try {
      const [chapterResponse, imagesResponse] = await Promise.all([
        $api.get(`/projects/${projectId}/chapters/${chapterId}`, {
          signal: abortController?.signal
        }),
        $api.get(`/projects/${projectId}/chapters/${chapterId}/cropped-memo-image`, {
          signal: abortController?.signal
        })
      ]);

      if (abortController?.signal.aborted) {
        return null;
      }

      const updatedChapter = (chapterResponse.data as { data: TChapter }).data;
      const croppedMemoImages = imagesResponse.data as TCroppedMemoImage[];

      if (!updatedChapter.activeMemo) return null;
      set((state) => {
        return {
          projectChapters: state.projectChapters.map((chapter) =>
            chapter.id === Number(chapterId)
              ? {
                  ...chapter,
                  activeMemo: chapter.activeMemo
                    ? {
                        ...chapter.activeMemo,
                        croppedMemoImages,
                        contentUpdatedAt: updatedChapter.activeMemo?.contentUpdatedAt ?? new Date().toISOString()
                      }
                    : null
                }
              : chapter
          )
        };
      });

      updateChapterContent(chapterId, updatedChapter.activeMemo.mdContent);
      return updatedChapter.activeMemo.mdContent;
    } catch (error: unknown) {
      if (!(error instanceof Error) || error.message !== "canceled") {
        console.error("Failed to sync chapter content:", error);
      }
    } finally {
      const { activeSyncRequests } = get();
      const newActiveSyncRequests = new Set(activeSyncRequests);
      newActiveSyncRequests.delete(requestKey);
      set({ activeSyncRequests: newActiveSyncRequests });
    }
    return null;
  },
  getChapterContent: (chapterId) => {
    const { projectChapters } = get();
    const chapter = projectChapters.find((c) => c.id === Number(chapterId));
    return chapter?.activeMemo?.mdContent || null;
  },

  updateChapterContentUpdatedAt: (chapterId, contentUpdatedAt) => {
    set((state) => {
      const updatedChapters = state.projectChapters.map((chapter) =>
        chapter.id === Number(chapterId) && chapter.activeMemo
          ? { ...chapter, activeMemo: { ...chapter.activeMemo, contentUpdatedAt } }
          : chapter
      );
      return {
        projectChapters: updatedChapters,
        activeChapter:
          state.activeChapter?.id === Number(chapterId) && state.activeChapter?.activeMemo
            ? { ...state.activeChapter, activeMemo: { ...state.activeChapter.activeMemo, contentUpdatedAt } }
            : state.activeChapter
      };
    });
  }
}));
